(() => {
  const imageSelector = '.sp-variant-thumbnail__image';
  const darkClass = 'sp-variant-thumbnail--dark';
  const darkCheckClass = 'sp-variant-thumbnail--dark-check';
  const thumbnailWidths = [86, 129, 172, 240];

  const hasDarkBackground = (pixels) => {
    let luminanceTotal = 0;
    let darkPixels = 0;
    let sampledPixels = 0;

    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = pixels[index + 3] / 255;

      if (alpha < 0.08) continue;

      const red = pixels[index] / 255;
      const green = pixels[index + 1] / 255;
      const blue = pixels[index + 2] / 255;
      const luminance = (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);

      luminanceTotal += luminance;
      darkPixels += luminance < 0.46 ? 1 : 0;
      sampledPixels += 1;
    }

    if (!sampledPixels) return false;

    return (luminanceTotal / sampledPixels) < 0.56 || (darkPixels / sampledPixels) > 0.52;
  };

  const classifyThumbnail = (image) => {
    const tile = image.closest('.sp-variant-thumbnail');

    if (!tile || !image.naturalWidth || tile.dataset.contrastChecked === 'true') {
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const size = 16;
      const context = canvas.getContext('2d', { willReadFrequently: true });

      if (!context) return;

      canvas.width = size;
      canvas.height = size;
      context.drawImage(image, 0, 0, size, size);

      const captionTop = Math.floor(size * 0.55);
      const checkLeft = Math.floor(size * 0.58);
      const checkSize = size - checkLeft;
      const captionPixels = context.getImageData(0, captionTop, size, size - captionTop).data;
      const checkPixels = context.getImageData(checkLeft, 0, checkSize, checkSize).data;

      tile.classList.toggle(darkClass, hasDarkBackground(captionPixels));
      tile.classList.toggle(darkCheckClass, hasDarkBackground(checkPixels));
      tile.dataset.contrastChecked = 'true';
    } catch (error) {
      // If pixel sampling is blocked, retain the high-contrast light fallback.
      tile.dataset.contrastChecked = 'true';
    }
  };

  const prepareThumbnail = (image) => {
    if (image.complete && image.naturalWidth) {
      classifyThumbnail(image);
      return;
    }

    image.addEventListener('load', () => classifyThumbnail(image), { once: true });
  };

  const getProduct = () => {
    const script = document.querySelector('script[id^="ProductJson-"]');

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  const getVariantImageSrc = (variant) => {
    if (!variant) return '';

    return variant.featured_image?.src || variant.featured_media?.preview_image?.src || '';
  };

  const imageUrlWithWidth = (src, width) => {
    if (!src) return '';

    const absoluteSrc = src.startsWith('//') ? `https:${src}` : src;

    try {
      const url = new URL(absoluteSrc, window.location.origin);
      url.searchParams.set('width', String(width));
      return `${url.protocol}//${url.host}${url.pathname}?${url.searchParams.toString()}`;
    } catch (error) {
      return src;
    }
  };

  const imagePath = (src) => {
    if (!src) return '';

    try {
      const absoluteSrc = src.startsWith('//') ? `https:${src}` : src;
      return new URL(absoluteSrc, window.location.origin).pathname;
    } catch (error) {
      return src;
    }
  };

  const getSelectedOptions = (root) => {
    const selected = {};

    root.querySelectorAll('.js-single-option-selector').forEach((input) => {
      const optionKey = input.getAttribute('data-index');

      if (!optionKey) return;

      if (input.tagName === 'SELECT') {
        selected[optionKey] = input.value;
        return;
      }

      if (input.checked) {
        selected[optionKey] = input.value;
      }
    });

    return selected;
  };

  const findVariantForOptions = (product, option1, option2, option3) => {
    const matchesImage = (variant) => Boolean(getVariantImageSrc(variant));
    const exactMatch = product.variants.find((variant) => {
      if (option1 && variant.option1 !== option1) return false;
      if (option2 && variant.option2 !== option2) return false;
      if (option3 && variant.option3 !== option3) return false;
      return matchesImage(variant);
    });

    if (exactMatch) return exactMatch;

    return product.variants.find((variant) => {
      if (option2 && variant.option2 !== option2) return false;
      if (option3 && variant.option3 !== option3) return false;
      return matchesImage(variant);
    });
  };

  const applyThumbnailImage = (image, src) => {
    if (!image || !src) return;

    if (imagePath(image.currentSrc || image.src) === imagePath(src)) return;

    const tile = image.closest('.sp-variant-thumbnail');
    const srcset = thumbnailWidths
      .map((width) => `${imageUrlWithWidth(src, width)} ${width}w`)
      .join(', ');

    image.src = imageUrlWithWidth(src, 240);
    image.srcset = srcset;
    image.sizes = '86px';

    if (tile) {
      tile.classList.remove(darkClass, darkCheckClass);
      delete tile.dataset.contrastChecked;
    }

    prepareThumbnail(image);
  };

  const updateColorThumbnails = (root = document) => {
    const product = getProduct();

    if (!product?.variants?.length) return;

    root.querySelectorAll('.variants-wrapper--color').forEach((wrapper) => {
      const form = wrapper.closest('form') || root;
      const selected = getSelectedOptions(form);

      wrapper.querySelectorAll('.js-single-option-selector').forEach((input) => {
        const colorValue = input.value;
        const variant = findVariantForOptions(
          product,
          selected.option1,
          colorValue,
          selected.option3
        );
        const image = input.parentElement?.querySelector(imageSelector);

        applyThumbnailImage(image, getVariantImageSrc(variant));
      });
    });
  };

  const initialize = (root = document) => {
    updateColorThumbnails(root);
    root.querySelectorAll(imageSelector).forEach(prepareThumbnail);
  };

  const handleOptionChange = (event) => {
    if (!event.target.closest?.('.js-single-option-selector')) return;

    updateColorThumbnails(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('change', handleOptionChange);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
